import React, {
  Ref,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./style.scss";
import TextTitle from "../common/ui/typography/TextTitle";
import Button from "../common/ui/buttons/Button";
import useStore from "./store";
import Icon from "../common/ui/Icon";
import TextCaption from "../common/ui/typography/TextCaption";
import ButtonGroupTab from "../common/ui/buttonGroupTab/ButtonGroupTab";
import DatePickerInput from "../common/form/DatePickerInput";
import CompaniesStatistics from "./components/companiesStatistics/CompaniesStatistics";
import { useStatisticsList } from "../../hooks/statistics/useStatisticsList";
import moment from "moment";
import { TStatistics } from "../../api/statistics/types";
import { useFinancialMetrics } from "../../hooks/statistics/useFinancialMetrics";
import { Tabs } from "../common/ui/tabs/Tabs";
import { Tab } from "../common/ui/tabs/Tab";
import { TIconsNames } from "../common/iconSets";
import { statistics } from "../../api/statistics";
import AsyncSelect from "react-select/async";
import Select, { SingleValue } from "react-select";
import { useGlobalStore } from "../../store";
import { debounce } from "lodash";
import { useQuery } from "react-query";
import { TruckPieChart } from "./components/PieChart/PieChart";
import { companyFeatures } from "../../config/company";

const SkeletonCard = () => {
  return (
    <div className="overview__card skeleton">
      <div className="overview__card__header">
        <div className="skeleton__text skeleton__text--title"></div>
        <div className="skeleton__icon"></div>
      </div>
      <div className="overview__card__body">
        <div className="skeleton__text skeleton__text--large"></div>
        <div className="d-flex gap-1">
          <div className="skeleton__icon"></div>
          <div className="skeleton__text skeleton__text--caption"></div>
          <div className="skeleton__text skeleton__text--caption"></div>
        </div>
      </div>
    </div>
  );
};
const statisticTypes: string[] = [
  "company",
  "subscription",
  "vehicle",
  "driver",
  "device",
  "user",
  "all",
  "dot",
];

type OptionType = {
  label: string;
  value: boolean;
};

const options: OptionType[] = [
  { label: "Active", value: true },
  { label: "All", value: false },
];

const customStyles = {
  control: (base: any, state: any) => ({
    ...base,
    borderRadius: 8,
    borderColor: state.isFocused ? "#ffb94a" : "var(--theme-color-grey-2)",
    boxShadow: state.isFocused ? "0 0 0 1px #ffb94a" : "none",
    backgroundColor: "var(--theme-input-bg)",
    color: "var(--theme-color-primary-text)",
    "&:hover": {
      borderColor: "#ffb94a",
    },
    minWidth: 120,
    fontSize: 14,
  }),
  menu: (base: any) => ({
    ...base,
    borderRadius: 8,
    border: "1px solid var(--theme-color-grey-2)",
    backgroundColor: "var(--theme-color-bg-elevated)",
    boxShadow: "var(--theme-box-shadow-dropdown)",
    overflow: "hidden",
  }),
  menuList: (base: any) => ({
    ...base,
    backgroundColor: "var(--theme-color-bg-elevated)",
    color: "var(--theme-color-primary-text)",
  }),
  singleValue: (base: any) => ({
    ...base,
    color: "var(--theme-color-primary-text)",
  }),
  multiValue: (base: any) => ({
    ...base,
    backgroundColor: "var(--theme-color-grey-1)",
  }),
  multiValueLabel: (base: any) => ({
    ...base,
    color: "var(--theme-color-primary-text)",
  }),
  multiValueRemove: (base: any) => ({
    ...base,
    color: "var(--theme-color-grey-3)",
    ":hover": {
      backgroundColor: "var(--theme-color-grey-2)",
      color: "var(--theme-color-primary-text)",
    },
  }),
  placeholder: (base: any) => ({
    ...base,
    color: "var(--theme-color-grey-3)",
  }),
  input: (base: any) => ({
    ...base,
    color: "var(--theme-color-primary-text)",
  }),
  indicatorSeparator: (base: any) => ({
    ...base,
    backgroundColor: "var(--theme-color-grey-2)",
  }),
  dropdownIndicator: (base: any) => ({
    ...base,
    color: "var(--theme-color-grey-3)",
    ":hover": {
      color: "var(--theme-color-primary-text)",
    },
  }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#ffb94a"
      : state.isFocused
        ? "var(--theme-color-grey-1)"
        : "transparent",
    color: state.isSelected ? "#fff" : "var(--theme-color-primary-text)",
    "&:hover": {
      backgroundColor: "#ffe2a6",
      color: "#fff",
    },
  }),
};

const OverviewPage = () => {
  const [isActive, setIsActive] = useState<boolean>(true);
  const isInitialMount = useRef(true);
  const activeOption = useStore((state) => state.activeOption);
  const setActiveOption = useStore((state) => state.setActiveOption);
  const startDate = useStore((state) => state.startDate);
  const endDate = useStore((state) => state.endDate);
  const setStartDate = useStore((state) => state.setStartDate);
  const setEndDate = useStore((state) => state.setEndDate);
  const resetStore = useStore((state) => state.resetStore);
  const setDriverData = useStore((state) => state.setDriverData);
  const setVehicleData = useStore((state) => state.setVehicleData);
  const setCompanyData = useStore((state) => state.setCompanyData);
  const setDotData = useStore((state) => state.setDotData);
  const setSubscriptionData = useStore((state) => state.setSubscriptionData);
  const setContractData = useStore((state) => state.setContractData);
  const setSubscriptionCompanyData = useStore(
    (state) => state.setSubscriptionCompanyData,
  );
  const setAll = useStore((state) => state.setAll);
  const setUserData = useStore((state) => state.setUserData);
  const setDeviceData = useStore((state) => state.setDeviceData);
  const setIsLoadingData = useStore((state) => state.setIsLoadingData);
  const handleTabChange = useStore((state) => state.handleTabChange);
  const activeTabIndex = useStore((state) => state.activeTabIndex);
  const selectedCompany = useGlobalStore((state) => state.selectedCompany);
  const [companyId, setCompanyId] = useState([]);

  const {
    data,
    isLoading,
    refetch,
  }: {
    data:
      | {
          driverData: TStatistics;
          vehicleData: TStatistics;
          companyData: TStatistics;
          subscriptionData: TStatistics;
          contractData: TStatistics;
          dotData: TStatistics;
          allSubscriptionData: TStatistics;
          subscriptionCompanyData: TStatistics;
          userData: TStatistics;
          deviceData: TStatistics;
        }
      | undefined;
    refetch: () => void;
    isLoading: boolean;
  } = useStatisticsList(
    moment(startDate).toISOString(),
    moment(endDate).toISOString(),
    statisticTypes[activeTabIndex] || "",
    activeTabIndex === 6 ? companyId : undefined,
  );

  const { data: financial } = useFinancialMetrics(isActive);
  const { data: vehiclePieChart, isLoading: vehiclePieChartLoading } = useQuery(
    ["vehiclePieChart", { selectedCompany, isActive }],
    () => statistics.getCompanyByEld(selectedCompany, isActive, "vehicle"),
  );
  const { data: driverPieChart, isLoading: driverPieChartLoading } = useQuery(
    ["driverPieChart", { selectedCompany, isActive }],
    () => statistics.getCompanyByEld(selectedCompany, isActive, "driver"),
  );

  useEffect(() => {
    setIsLoadingData(isLoading);
    if (activeTabIndex === 6 && companyId?.length && data && !isLoading) {
      setAll(data);
      return;
    }
    if (data?.driverData) {
      setDriverData(data?.driverData);
    }
    if (data?.vehicleData) {
      setVehicleData(data?.vehicleData);
    }
    if (data?.companyData) {
      setCompanyData(data?.companyData);
    }
    if (data?.contractData) {
      setContractData(data?.contractData);
    }
    if (data?.subscriptionData) {
      setSubscriptionData(data?.subscriptionData);
    }
    if (data?.subscriptionCompanyData) {
      setSubscriptionCompanyData(data?.subscriptionCompanyData);
    }
    if (data?.userData) {
      setUserData(data?.userData);
    }
    if (data?.dotData) {
      setDotData(data?.dotData);
    }
    if (data?.deviceData) {
      setDeviceData(data?.deviceData);
    }
  }, [data, isLoading, companyId]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      return () => {
        resetStore();
      };
    }
  }, []);

  const handleOptionSelect = (selectedOption: {
    title: string;
    value: string;
  }) => {
    const endOfDay = moment().endOf("day").toDate();
    switch (selectedOption.value) {
      case "day":
        setStartDate(moment(endOfDay).add(-1, "day").toDate());
        setEndDate(endOfDay);
        break;
      case "week":
        setStartDate(moment(endOfDay).add(-1, "week").toDate());
        setEndDate(endOfDay);
        break;
      case "month":
        setStartDate(moment(endOfDay).add(-1, "month").toDate());
        setEndDate(endOfDay);
        break;
      case "year":
        setStartDate(moment(endOfDay).add(-1, "year").toDate());
        setEndDate(endOfDay);
    }
    setActiveOption(selectedOption);
  };
  const tabs = [
    { title: "Day", value: "day" },
    { title: "Week", value: "week" },
    { title: "Month", value: "month" },
    { title: "Year", value: "year" },
    { title: "Custom time range", value: "custom" },
  ];

  type TFinancialKeys =
    | "company"
    | "vehicle"
    | "driver"
    | "user"
    | "codriver"
    | "dot"
    | "subscription"
    | "contract"
    | "allSubscription";
  const financialKeys: TFinancialKeys[] = [
    "company",
    "vehicle",
    "driver",
    "user",
    "codriver",
    "dot",
    "contract",
    "subscription",
    "allSubscription",
  ];
  const cardIcons: Record<TFinancialKeys, TIconsNames> = {
    company: "building-filled",
    vehicle: "truck-regular-filled",
    driver: "person",
    codriver: "person",
    user: "people-filled",
    dot: "people-filled",
    contract: "dolla-filled",
    allSubscription: "dolla-filled",
    subscription: "dolla-filled",
  };

  type CompanyOption = {
    value: number;
    label: string;
  };

  const loadCompanyOptions = async (
    inputValue: string,
    callback: (options: CompanyOption[]) => void,
  ): Promise<CompanyOption[]> => {
    try {
      const { data } = await statistics.companiesList({
        searchName: inputValue,
        page: 1,
        perPage: 50,
        selectedCompany,
      });

      const options: CompanyOption[] = data.map(
        (company: { id: number; name: string }) => ({
          value: company.id,
          label: company.name,
        }),
      );

      callback(options);
      return options;
    } catch (error) {
      console.error("Error loading companies:", error);
      const emptyOptions: CompanyOption[] = [];
      callback(emptyOptions);
      return emptyOptions;
    }
  };

  const debouncedLoadOptions = useMemo(
    () =>
      debounce(
        (inputValue: string, callback: (options: CompanyOption[]) => void) => {
          loadCompanyOptions(inputValue, callback);
        },
        500,
      ),
    [],
  );

  const loadOptions = useCallback(
    (inputValue: string, callback: (options: CompanyOption[]) => void) => {
      debouncedLoadOptions(inputValue, callback);
    },
    [debouncedLoadOptions],
  );

  const onChange = (option: any) => {
    setCompanyId(option.map((el: any) => el.value));
  };

  const handleSelectChange = (selectedOption: SingleValue<OptionType>) => {
    if (selectedOption) {
      setIsActive(selectedOption.value);
    }
  };

  const getTitle = (key: TFinancialKeys, isActive: boolean): string => {
    const title: Record<TFinancialKeys, string> = {
      company: isActive ? "Active Companies" : "Total Companies",
      vehicle: isActive ? "Active Vehicles" : "Total Vehicles",
      driver: isActive ? "Active Drivers" : "Total Drivers",
      user: "Total Active Users",
      codriver: isActive ? "Active Co-Drivers" : "Total Co-Drivers",
      contract: "Contract Subscriptions",
      subscription: "Non-contract Subscriptions",

      allSubscription: "All Subscriptions",
      dot: "Total DOT Inspections",
    };
    return title[key] || "";
  };

  return (
    <div className="overview">
      <div className="overview__header">
        <TextTitle level="large">Today, {moment().format("D MMMM")}</TextTitle>
        <div className="d-flex gap-3">
          {companyFeatures.showActiveFilter && (
            <Select<OptionType, false>
              options={options}
              value={options.find((option) => option.value === isActive)}
              onChange={handleSelectChange}
              styles={customStyles}
            />
          )}
          <Button beforeIcon="arrow-clockwise" onClick={refetch}>
            Refresh
          </Button>
        </div>
      </div>
      {financial?.driverData &&
      financial?.companyData &&
      financial?.codriverData &&
      financial?.vehicleData ? (
        <div className="overview__body">
          <div className="overview__body__infocpm">
            {financialKeys.map((key) => (
              <div className="overview__card" key={key}>
                <div className="overview__card__header">
                  <Icon color="var(--theme-color-grey-3)" icon={cardIcons[key]} />
                  <TextTitle level="2">{getTitle(key, isActive)}</TextTitle>
                </div>
                <div className="overview__card__body">
                  <TextTitle level="large">
                    {financial[`${key}Data`]?.total?.toLocaleString() || 0}
                  </TextTitle>
                  <div
                    className="d-flex gap-1"
                    style={{
                      color:
                        financial[`${key}Data`]?.totalDifference > 0
                          ? "#27AE60"
                          : financial[`${key}Data`]?.totalDifference === 0
                            ? "var(--theme-color-grey-3)"
                            : "#F64747",
                    }}
                  >
                    <Icon
                      icon={
                        financial[`${key}Data`]?.totalDifference > 0
                          ? "arrow-trending-filled"
                          : financial[`${key}Data`]?.totalDifference === 0
                            ? "arrow-right-filled"
                            : "arrow-trending-filled-red"
                      }
                    />
                    <TextCaption level="2">
                      {financial[`${key}Data`]?.totalDifference > 0 ? "+" : ""}
                      {parseFloat(
                        financial[`${key}Data`]?.totalDifference?.toFixed(1) ||
                          0,
                      )}{" "}
                      (
                      {parseFloat(
                        financial[`${key}Data`]?.totalPercentage?.toFixed(1) ||
                          0,
                      )}
                      %)
                    </TextCaption>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {/* <div className='overview__body__fmetrics'>
              <div className='overview__body__fmetrics--header'>
                <TextTitle level='2'>Financial Metrics</TextTitle>
                {/* <Icon icon='info' /> */}
          {/* </div>
              <div className='d-flex w-100 gap-3 flex-wrap'>
                <div className='overview__body__fmetrics__info'>
                  <TextCaption level='4'>
                    Daily Recurring Revenue (DRR)
                  </TextCaption>
                  <TextTitle level='1'>
                    $
                    {financial?.financialData?.daily?.toLocaleString() ||
                      "000,000.00"}
                  </TextTitle>
                </div>
                <div className='overview__body__fmetrics__info'>
                  <TextCaption level='4'>
                    Monthly Recurring Revenue (MRR)
                  </TextCaption>
                  <TextTitle level='1'>
                    $
                    {financial?.financialData?.monthly?.toLocaleString() ||
                      "000,000.00"}
                  </TextTitle>
                </div>
                <div className='overview__body__fmetrics__info'>
                  <TextCaption level='4'>
                    Annual Recurring Revenue (ARR)
                  </TextCaption>
                  <TextTitle level='1'>
                    $
                    {financial?.financialData?.annual?.toLocaleString() ||
                      "000,000.00"}
                  </TextTitle>
                </div>
              </div> */}
          {/* </div> */}
        </div>
      ) : (
        <div className="overview__body">
          <div className="overview__body__infocpm">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        </div>
      )}
      <div className="d-flex">
        <TruckPieChart
          rawData={vehiclePieChart?.data}
          isLoading={vehiclePieChartLoading || driverPieChartLoading}
        />
        <TruckPieChart
          rawData={driverPieChart?.data}
          isLoading={vehiclePieChartLoading || driverPieChartLoading}
        />
      </div>

      <div style={{ marginLeft: 24 }}>
        <TextTitle level="1">Statistics</TextTitle>
      </div>

      <Tabs modelValue={activeTabIndex} onTabChange={handleTabChange}>
        <Tab icon="building" activeIcon="building-filled" text="Companies" />
        <Tab icon="dolla" activeIcon="dolla-filled" text="Subscriptions" />
        <Tab
          icon="truck-regular"
          activeIcon="truck-regular-filled"
          text="Vehicles"
        />
        <Tab icon="person2" activeIcon="person2-filled" text="Drivers" />
        <Tab
          icon="phone-tablet"
          activeIcon="phone-tablet-filled"
          text="App Users"
        />
        <Tab icon="people" activeIcon="people-filled" text="User Engagements" />
        {selectedCompany !== "all" && (
          <Tab icon="building" activeIcon="building-filled" text="By Company" />
        )}

        <Tab icon="person2" activeIcon="person2-filled" text="DOT Inspection" />
      </Tabs>
      <div className="overview__statistics">
        <div className="overview__statistics--buttons">
          {activeTabIndex === 6 && (
            <div style={{ width: 320 }}>
              <AsyncSelect
                cacheOptions
                defaultOptions
                loadOptions={loadOptions}
                onChange={onChange}
                isMulti
                placeholder="Select a company..."
                styles={customStyles}
              />
            </div>
          )}
          {activeOption && (
            <ButtonGroupTab
              options={tabs}
              defaultActive={activeOption}
              onOptionSelect={handleOptionSelect}
            />
          )}
          {activeOption?.value === "custom" && (
            <div className="date-pickers d-flex">
              <DatePickerInput
                value={startDate}
                onChange={(date: Date | null) => setStartDate(date)}
                dateFormat="MMM d, yyyy"
                customClassName="date-picker-input custom-datepicker"
                maxDate={endDate || new Date()}
                placeholder="From"
                afterIcon="chevron-down"
                beforeIcon="calendar"
              />
              <DatePickerInput
                value={endDate}
                onChange={(date) => setEndDate(date)}
                dateFormat="MMM d, yyyy"
                customClassName="date-picker-input custom-datepicker"
                maxDate={new Date()}
                minDate={startDate || undefined}
                placeholder="To"
                afterIcon="chevron-down"
                beforeIcon="calendar"
              />
            </div>
          )}
        </div>
        <CompaniesStatistics statisticType={statisticTypes[activeTabIndex]} />
      </div>
    </div>
  );
};

export default OverviewPage;
